<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Icons } from '@/models/icons.enum';
import type { SwiperContainer } from 'swiper/element/bundle';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import VideoPlayerPopup from './../VideoPlayerPopup.vue';
import SeasonVideo from './SeasonVideo.vue';

const props = defineProps<{ slides: NonNullable<ResolvedSelfSellInModel['seasonNewsSection']>['slides'] }>();

const { t } = useI18n();

const swiperRef = ref<SwiperContainer | null>(null);
const showPopup = ref(false);
const videoStartIndex = ref(0);

const onPlayVideo = (index: number) => {
  videoStartIndex.value = index;
  showPopup.value = true;
};

const nextSlide = () => swiperRef.value?.swiper?.slideNext();
const prevSlide = () => swiperRef.value?.swiper?.slidePrev();

const activeIndex = ref(0);

const isFirst = computed(() => activeIndex.value === 0);
const isLast = computed(() => activeIndex.value >= props.slides.length - 1);

function onSlideChange(event: any) {
  const swiper = event.detail[0];
  activeIndex.value = swiper.activeIndex;
}
</script>

<template>
  <div class="video-carousel">
    <swiper-container
      ref="swiperRef"
      data-cy="video-carousel"
      slides-per-view="1.3"
      space-between="6"
      :mousewheel-force-to-axis="true"
      :mousewheel-sensitivity="1"
      :grab-cursor="true"
      :free-mode="true"
      @swiperslidechange="onSlideChange"
      class="video-carousel__swiper">
      <swiper-slide v-for="(slide, index) in slides" :key="slide.title" class="video-carousel__slide">
        <SeasonVideo :slide="slide" @play="onPlayVideo(index)" />
      </swiper-slide>
    </swiper-container>

    <div class="video-carousel__controls" data-cy="video-carousel-controls">
      <div class="controls-wrapper">
        <BackstageButton
          class="nav-btn nav-btn--prev"
          :icon-left="Icons.CHEVRON_RIGHT"
          :variant="ButtonVariant.PRIMARY_INVERTED"
          :disabled="isFirst"
          :data-cy-button="'video-carousel-prev-btn'"
          @click="prevSlide" />
        <BackstageButton
          class="nav-btn nav-btn--next"
          :icon-left="Icons.CHEVRON_RIGHT"
          :variant="ButtonVariant.PRIMARY_INVERTED"
          :disabled="isLast"
          :data-cy-button="'video-carousel-next-btn'"
          @click="nextSlide" />
        <span class="video-count">{{
          t('viewSelfSellIn.video_carousel.video_count', { count: slides?.length || 0 })
        }}</span>
      </div>
    </div>

    <VideoPlayerPopup
      v-if="showPopup"
      :videos="slides.map((v) => v.video)"
      :start-index="videoStartIndex"
      @close="showPopup = false"
      data-cy="video-player-popup" />
  </div>
</template>

<style lang="scss" scoped>
.video-carousel {
  width: 100%;
  touch-action: pan-y;

  &__swiper {
    width: 100%;
    overflow: visible;
  }

  &__slide {
    width: auto;
  }

  &__controls {
    margin-top: 10px;
    display: flex;
    align-items: center;
  }
}

.controls-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;

  .nav-btn {
    :deep(button) {
      width: 40px;
      height: 40px;
      background-color: var(--pale-gray-color);
      .icon {
        background-color: var(--shade-5);
      }
      &:hover {
        background-color: var(--pale-gray-color) !important;

        .icon {
          background-color: var(--secondary-color);
        }
      }

      &:disabled {
        opacity: 1;
        background-color: var(--gray-color) !important;
        .icon {
          background-color: var(--shade-4);
        }

        &:hover {
          .icon {
            background-color: var(--shade-5);
          }
        }
      }
    }

    &--prev :deep(button) {
      transform: rotate(180deg);
    }
  }

  .video-count {
    font-size: 1.125rem;
    color: var(--primary-text-color);
    font-weight: 400;
    padding-left: 14px;
    opacity: 0.5;
  }
}
</style>
